import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  PaperPlaneTilt,
  Lightning,
  WarningCircle,
  UploadSimple,
  CheckCircle,
  X,
  CircleNotch,
  Image as ImageIcon,
} from "@phosphor-icons/react";

function parseTemplate(components) {
  let header = null;
  let body = "";
  let footer = null;
  for (const c of components || []) {
    const t = (c.type || "").toUpperCase();
    if (t === "HEADER") {
      const fmt = (c.format || "TEXT").toUpperCase();
      header = {
        format: fmt,
        text: c.text || null,
        requiresMedia: ["IMAGE", "VIDEO", "DOCUMENT"].includes(fmt),
      };
    } else if (t === "BODY") {
      body = c.text || "";
    } else if (t === "FOOTER") {
      footer = c.text || null;
    }
  }
  const matches = body.match(/{{\d+}}/g) || [];
  return { header, body, footer, varCount: matches.length };
}

export default function NewChatDialog({ open, onClose }) {
  const [phone, setPhone] = useState("");
  const [templates, setTemplates] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [selected, setSelected] = useState("");
  const [params, setParams] = useState([]);
  const [headerFile, setHeaderFile] = useState(null);
  const [headerMediaId, setHeaderMediaId] = useState("");
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState(null);
  const [headerUploading, setHeaderUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const headerFileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api
      .get("/whatsapp/templates")
      .then((r) => {
        setTemplates(r.data.templates || []);
        setConfigured(r.data.configured !== false);
      })
      .catch(() => setConfigured(false));
  }, [open]);

  const tpl = templates.find((t) => `${t.name}:${t.language}` === selected);
  const tplStructure = tpl ? parseTemplate(tpl.components) : null;

  // Reset variables and header when template changes
  useEffect(() => {
    setParams(Array(tplStructure?.varCount || 0).fill(""));
    setHeaderFile(null);
    setHeaderMediaId("");
    if (headerPreviewUrl) URL.revokeObjectURL(headerPreviewUrl);
    setHeaderPreviewUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Reset everything on close
  useEffect(() => {
    if (open) return;
    setPhone("");
    setSelected("");
    setParams([]);
    setHeaderFile(null);
    setHeaderMediaId("");
    if (headerPreviewUrl) URL.revokeObjectURL(headerPreviewUrl);
    setHeaderPreviewUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const uploadHeader = async (file) => {
    if (!file) return;
    setHeaderUploading(true);
    setHeaderFile(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("wh_token");
      const res = await fetch(`${API_BASE}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        let msg = "Upload failed";
        try {
          const err = await res.json();
          const d = err.detail;
          if (typeof d === "string") msg = d;
          else if (d?.message) msg = `${d.message}${d.code ? ` (${d.code})` : ""}`;
        } catch (_) {}
        throw new Error(msg);
      }
      const data = await res.json();
      setHeaderMediaId(data.media_id);
      const url = URL.createObjectURL(file);
      setHeaderPreviewUrl(url);
      toast.success("Header media uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
      setHeaderFile(null);
    } finally {
      setHeaderUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!tpl) return toast.error("Select a template");
    if (!phone.trim()) return toast.error("Enter phone number");
    if (tplStructure?.header?.requiresMedia && !headerMediaId) {
      return toast.error(`Template requires a ${tplStructure.header.format.toLowerCase()} — upload karein`);
    }
    setSending(true);
    try {
      const payload = {
        to: phone.replace(/\D/g, ""),
        template_name: tpl.name,
        language: tpl.language,
        body_params: params,
      };
      if (headerMediaId && tplStructure?.header?.requiresMedia) {
        payload.header_kind = tplStructure.header.format.toLowerCase();
        payload.header_media_id = headerMediaId;
      }
      await api.post("/chat/initiate", payload);
      toast.success("Template sent!");
      onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed");
    } finally {
      setSending(false);
    }
  };

  const headerAccept =
    tplStructure?.header?.format === "IMAGE" ? "image/*" :
    tplStructure?.header?.format === "VIDEO" ? "video/*" :
    tplStructure?.header?.format === "DOCUMENT" ? ".pdf,application/pdf" :
    "*/*";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="new-chat-dialog">
        <DialogHeader>
          <DialogTitle className="heading">Start a new conversation</DialogTitle>
          <DialogDescription>
            Cold outreach requires an approved Meta template (24h rule).
          </DialogDescription>
        </DialogHeader>

        {!configured && (
          <div className="rounded-lg bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] p-3 text-xs flex gap-2">
            <WarningCircle size={16} className="shrink-0 mt-0.5" />
            <div>
              <b>WHATSAPP_WABA_ID not set.</b> Add it in backend .env / Secrets to
              fetch Meta-approved templates.
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Phone number (with country code, no +)</Label>
            <Input
              required
              placeholder="919876543210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="new-chat-phone"
              inputMode="numeric"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Template ({templates.length} approved)</Label>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-[var(--border)] bg-[var(--surface)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--fg)]/20"
              data-testid="new-chat-template"
            >
              <option value="">
                {templates.length ? "Select a template" : "No templates available"}
              </option>
              {templates.map((t) => (
                <option key={`${t.name}:${t.language}`} value={`${t.name}:${t.language}`}>
                  {t.name} · {t.language} · {t.category}
                </option>
              ))}
            </select>
          </div>

          {/* Template preview */}
          {tplStructure && (
            <div className="rounded-md bg-[var(--bg)] border border-[var(--border)] overflow-hidden text-xs">
              {/* Header media preview area */}
              {tplStructure.header?.format === "IMAGE" && (
                <div className="bg-[#E5E7EB] aspect-video flex items-center justify-center overflow-hidden">
                  {headerPreviewUrl ? (
                    <img src={headerPreviewUrl} alt="header" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[11px] text-[var(--muted)] flex items-center gap-1.5">
                      <ImageIcon size={14} /> Image header — please upload below
                    </span>
                  )}
                </div>
              )}
              {tplStructure.header?.format === "VIDEO" && (
                <div className="bg-[#1F2937] aspect-video flex items-center justify-center text-white/70 text-[11px]">
                  {headerPreviewUrl ? <video src={headerPreviewUrl} className="w-full h-full" muted /> : "Video header — upload below"}
                </div>
              )}
              {tplStructure.header?.format === "DOCUMENT" && (
                <div className="bg-[#F3F4F6] p-2 text-[11px] flex items-center gap-1.5 border-b border-[var(--border)]">
                  <UploadSimple size={12} /> {headerPreviewUrl ? "Document attached" : "Document header — upload below"}
                </div>
              )}
              {tplStructure.header?.format === "TEXT" && tplStructure.header.text && (
                <div className="px-3 pt-2 font-bold">{tplStructure.header.text}</div>
              )}
              <div className="px-3 py-2 whitespace-pre-wrap">{tplStructure.body}</div>
              {tplStructure.footer && (
                <div className="px-3 pb-2 text-[10px] text-[var(--muted)]">{tplStructure.footer}</div>
              )}
            </div>
          )}

          {/* Header media upload */}
          {tplStructure?.header?.requiresMedia && (
            <div className="space-y-1.5">
              <Label className="text-xs">
                Header {tplStructure.header.format.toLowerCase()} *
              </Label>
              <input
                ref={headerFileRef}
                type="file"
                accept={headerAccept}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadHeader(f);
                  e.target.value = "";
                }}
                className="hidden"
                data-testid="new-chat-header-input"
              />
              <button
                type="button"
                onClick={() => headerFileRef.current?.click()}
                disabled={headerUploading}
                className="w-full border border-dashed border-[var(--border)] rounded-md p-3 text-xs hover:bg-[var(--bg)] flex items-center justify-center gap-2"
                data-testid="new-chat-header-button"
              >
                {headerUploading ? (
                  <><CircleNotch size={14} className="animate-spin" /> Uploading…</>
                ) : headerMediaId ? (
                  <>
                    <CheckCircle size={14} weight="fill" className="text-[#10B981]" />
                    <span className="font-medium">{headerFile?.name || "Uploaded"}</span>
                    <span className="text-[var(--muted)]">— click to replace</span>
                  </>
                ) : (
                  <>
                    <UploadSimple size={14} />
                    Upload {tplStructure.header.format.toLowerCase()}
                    <span className="text-[var(--muted)]">
                      ({tplStructure.header.format === "IMAGE" ? "JPG/PNG, ≤5MB" :
                        tplStructure.header.format === "VIDEO" ? "MP4, ≤16MB" :
                        "PDF, ≤100MB"})
                    </span>
                  </>
                )}
              </button>
              {headerMediaId && (
                <button
                  type="button"
                  onClick={() => {
                    setHeaderFile(null); setHeaderMediaId("");
                    if (headerPreviewUrl) URL.revokeObjectURL(headerPreviewUrl);
                    setHeaderPreviewUrl(null);
                  }}
                  className="text-[10px] text-[#991B1B] flex items-center gap-1 hover:underline"
                >
                  <X size={10} /> Remove media
                </button>
              )}
            </div>
          )}

          {/* Body variables */}
          {params.map((p, i) => (
            <div key={i} className="space-y-1.5">
              <Label className="text-xs">Variable {`{{${i + 1}}}`}</Label>
              <Input
                required
                placeholder={`Value for {{${i + 1}}}`}
                value={p}
                onChange={(e) =>
                  setParams((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))
                }
              />
            </div>
          ))}

          <DialogFooter>
            <Button type="submit" disabled={sending} className="gap-1.5" data-testid="new-chat-send">
              {sending ? <Lightning size={14} /> : <PaperPlaneTilt size={14} weight="fill" />}
              {sending ? "Sending…" : "Send template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
